import styled from '@emotion/styled';
import {
  Check,
  Close,
  DeleteOutline,
  DownloadOutlined,
  EditOutlined,
  FlashOnOutlined,
  FormatListBulletedOutlined,
  LowPriority,
  QrCodeOutlined,
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import useChangeBranchCodingProfile from 'api/codingProfile/useChangeBranchCodingProfile/useChangeBranchCodingProfile';
import useMakeDefaultCodingBranch from 'api/codingProfile/useMakeDefaultCodingBranch/useMakeDefaultCodingBranch';
import useProductCoddingDelete from 'api/productDetail/useProductCoddingDelete/useProductCoddingDelete';
import DeleteModal from 'components/shared/DeleteModal/DeleteModal';
import ChangeCodingProfileModal from 'components/shared/Modals/productsList/ChangeCodingProfileModal/ChangeCodingProfileModal';
import MakeDefaultApproveModal from 'components/shared/Modals/productsList/MakeDefaultApproveModal/MakeDefaultApproveModal';
import { permissions } from 'hooks/permissions/permissions';
import { usePermissions } from 'hooks/permissions/usePermissions';
import React, { useCallback, useMemo, useState } from 'react';

export const CODE_LOGIC = {
  SKU: 'SKU',
  BatchNumber: 'BatchNumber',
  SerialNumber: 'SerialNumber',
};

const coddingPermissions =
  permissions.general.categories.globalSettings.productManagement.productList
    .detail.codding;

const AccordionCodingList = ({
  coddingList,
  getProductDetail,
  refreshProductBranchList,
  setSystemEdit,
  setManualEdit,
  setEditMapping,
  setCreateCoddingGuid,
}) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSetDefaultModalOpen, setIsSetDefaultModalOpen] = useState(false);
  const [changeProfileModalOpen, setChangeProfileModalOpen] = useState(false);
  const [deleteChosenLocation, setDeleteChosenLocation] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowInfo, setSelectedRowInfo] = useState(null);

  const { deleteLoading, deleteRequest } = useProductCoddingDelete();
  const { loading: isMakeDefaultLoading, makeDefault: makeDefaultRequest } =
    useMakeDefaultCodingBranch();
  const {
    changeBranchCodingProfile,
    loading: isChangeBranchCodingProfileLoading,
  } = useChangeBranchCodingProfile();

  const { can } = usePermissions();

  const openMenu = Boolean(anchorEl);

  const handleClickMenu = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowInfo(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const deleteItem = useCallback(
    row => () => {
      setDeleteChosenLocation(row);
      setIsDeleteModalOpen(true);
    },
    [],
  );

  const editItem = useCallback(
    row => () => {
      setManualEdit(row);
    },
    [setManualEdit],
  );

  const setDefaultCodeItem = useCallback(
    row => () => {
      setSelectedRowInfo(row);
      setIsSetDefaultModalOpen(true);
    },
    [],
  );

  const triggerBatchItem = useCallback(
    row => () => {
      // TODO: implement
    },
    [],
  );

  const showCodeListItem = useCallback(
    row => () => {
      // TODO: implement
    },
    [],
  );

  const downloadCodeListItem = useCallback(
    row => () => {
      // TODO: implement
    },
    [],
  );

  const showSerialListItem = useCallback(
    row => () => {
      // TODO: implement
    },
    [],
  );

  const downloadSerialListItem = useCallback(
    row => () => {
      // TODO: implement
    },
    [],
  );

  const changeProfileItem = useCallback(
    row => () => {
      setSelectedRowInfo(row);
      setChangeProfileModalOpen(true);
    },
    [],
  );

  const onDeleteSuccess = () => {
    setIsDeleteModalOpen(false);
    refreshProductBranchList();
  };

  const deleteHandle = id => {
    deleteRequest(id, onDeleteSuccess);
  };

  const makeDefaultHandle = () => {
    makeDefaultRequest({
      guid: selectedRowInfo?.id,
      onSuccess: () => {
        setIsSetDefaultModalOpen(false);
        refreshProductBranchList();
      },
    });
  };

  const changeProfileHandle = selectedProfile => {
    changeBranchCodingProfile({
      guid: selectedRowInfo?.id,
      newProfile: selectedProfile,
      onSuccess: () => {
        setChangeProfileModalOpen(false);
        refreshProductBranchList();
      },
    });
  };

  const handleEdit = useCallback(
    packItem => {
      const codingType = String(packItem?.type ?? '');
      const codingGuid = packItem?.id ?? packItem?.guid;

      switch (codingType) {
        case '0':
          setSystemEdit(codingGuid);
          return;

        case '1':
          editItem({ ...packItem, id: codingGuid, guid: codingGuid })();
          return;

        case '2':
          setEditMapping({ ...packItem, id: codingGuid, guid: codingGuid });
          break;
        default:
      }
    },
    [editItem, setEditMapping, setSystemEdit],
  );

  const menuItems = useMemo(
    () => [
      {
        label: 'حذف',
        permission: coddingPermissions.delete,
        icon: DeleteOutline,
        color: 'error',

        visible: row => !!row && row.type !== '0' && !row.isDefault,

        onClick: row => {
          handleCloseMenu();
          deleteItem(row)();
        },
      },

      {
        label: 'ویرایش',
        permission: coddingPermissions.edit,
        icon: EditOutlined,
        color: 'warning',

        visible: row => !!row && (row.logic === CODE_LOGIC.SKU || !row.logic),

        onClick: row => {
          handleCloseMenu();
          handleEdit(row);
        },
      },

      {
        label: 'تریگر',
        permission: coddingPermissions.batch.trigger,
        icon: FlashOnOutlined,
        color: 'secondary',

        visible: row => row?.logic === CODE_LOGIC.BatchNumber,

        onClick: row => {
          handleCloseMenu();
          triggerBatchItem(row)();
        },
      },

      {
        label: 'نمایش لیست کد',
        permission: coddingPermissions.batch.view,
        icon: FormatListBulletedOutlined,
        color: 'info',

        visible: row => row?.logic === CODE_LOGIC.BatchNumber,

        onClick: row => {
          handleCloseMenu();
          showCodeListItem(row)();
        },
      },

      {
        label: 'دانلود لیست کد',
        permission: coddingPermissions.batch.download,
        icon: DownloadOutlined,
        color: 'success',

        visible: row => row?.logic === CODE_LOGIC.BatchNumber,

        onClick: row => {
          handleCloseMenu();
          downloadCodeListItem(row)();
        },
      },

      {
        label: 'نمایش لیست سریال ها',
        permission: coddingPermissions.serial.view,
        icon: FormatListBulletedOutlined,
        color: 'info',

        visible: row => row?.logic === CODE_LOGIC.SerialNumber,

        onClick: row => {
          handleCloseMenu();
          showSerialListItem(row)();
        },
      },

      {
        label: 'دانلود لیست سریال ها',
        permission: coddingPermissions.serial.download,
        icon: DownloadOutlined,
        color: 'success',

        visible: row => row?.logic === CODE_LOGIC.SerialNumber,

        onClick: row => {
          handleCloseMenu();
          downloadSerialListItem(row)();
        },
      },

      {
        label: 'تبدیل به کد پیش‌فرض',
        permission: coddingPermissions.setDefault,
        icon: QrCodeOutlined,
        color: 'primary',

        visible: row =>
          !!row && row.type === '1' && row.isTitleRequired && !row.isDefault,

        onClick: row => {
          handleCloseMenu();
          setDefaultCodeItem(row)();
        },
      },

      {
        label: 'تغییر پروفایل',
        permission: coddingPermissions.profile.edit,
        icon: LowPriority,
        color: 'primary',

        visible: row => row && row.type === '1' && !row.isDefault,

        onClick: row => {
          handleCloseMenu();
          changeProfileItem(row)();
        },
      },
    ],
    [
      deleteItem,
      handleEdit,
      setDefaultCodeItem,
      triggerBatchItem,
      showCodeListItem,
      downloadCodeListItem,
      showSerialListItem,
      downloadSerialListItem,
      changeProfileItem,
    ],
  );

  const visibleMenuItems = useMemo(() => {
    return menuItems.filter(
      item => item.visible(selectedRowInfo) && can(item.permission),
    );
  }, [menuItems, selectedRowInfo, can]);

  return (
    <Wrapper>
      <Header>
        <Title>لیست کدینگ</Title>

        {can(coddingPermissions.create) && (
          <AddButton
            onClick={setCreateCoddingGuid}
            variant="contained"
            size="small"
          >
            <AddIcon />
          </AddButton>
        )}
      </Header>

      <TableContainer>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-root': {
                  borderColor: 'rgba(100, 100, 100, .2)',
                  background: 'rgba(180, 180, 180, 0.05)',
                },
              }}
            >
              <TableCell align="right" width={200}>
                کدینگ
              </TableCell>
              <TableCell align="right">نوع</TableCell>
              <TableCell align="right">عنوان</TableCell>
              <TableCell align="right">عنوان پروفایل</TableCell>
              <TableCell align="right">کد پیش‌فرض</TableCell>
              <TableCell align="left" width={100}>
                عملیات ها
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {coddingList?.map((packItem, index) => {
              const rowHasActions = menuItems.some(
                item => item.visible(packItem) && can(item.permission),
              );

              return (
                <TableRow key={packItem.id ?? index}>
                  <TableCell align="right">{packItem.codding}</TableCell>

                  <TableCell align="right">
                    {packItem.type === '0'
                      ? 'سیستمی'
                      : packItem.type === '1'
                      ? 'دستی'
                      : 'مپینگ'}
                  </TableCell>

                  <TableCell align="right">{packItem.display}</TableCell>
                  <TableCell align="right">{packItem.profileName}</TableCell>

                  <TableCell align="right">
                    {packItem.isDefault ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Check />
                      </Box>
                    ) : (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Close color="disabled" sx={{ opacity: 0.6 }} />
                      </Box>
                    )}
                  </TableCell>

                  <TableCell align="right" width={180}>
                    {rowHasActions ? (
                      <ActionContainer>
                        <IconButton
                          id={`${packItem.title}-button`}
                          aria-controls={
                            openMenu ? `${packItem.title}-menu` : undefined
                          }
                          aria-haspopup="true"
                          aria-expanded={openMenu ? 'true' : undefined}
                          onClick={e => handleClickMenu(e, packItem)}
                        >
                          <MenuIcon />
                        </IconButton>
                      </ActionContainer>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          {visibleMenuItems.map(item => {
            const Icon = item.icon;

            return (
              <MenuItem
                key={item.permission}
                onClick={() => item.onClick(selectedRowInfo)}
              >
                <Action>
                  <Typography fontSize={14}>{item.label}</Typography>

                  <Icon fontSize="small" color={item.color} />
                </Action>
              </MenuItem>
            );
          })}
        </Menu>
      </TableContainer>

      <DeleteModal
        open={isDeleteModalOpen}
        handleClose={() => setIsDeleteModalOpen(false)}
        title={deleteChosenLocation.title}
        locationId={deleteChosenLocation.id}
        deleteLoading={deleteLoading}
        onDelete={deleteHandle}
      />

      <MakeDefaultApproveModal
        open={isSetDefaultModalOpen}
        handleClose={() => setIsSetDefaultModalOpen(false)}
        isLoading={isMakeDefaultLoading}
        onConfirm={makeDefaultHandle}
      />

      <ChangeCodingProfileModal
        open={changeProfileModalOpen}
        onClose={() => setChangeProfileModalOpen(false)}
        selectedRowInfo={selectedRowInfo}
        isLoading={isChangeBranchCodingProfileLoading}
        onConfirm={changeProfileHandle}
      />
    </Wrapper>
  );
};

export default AccordionCodingList;

const Wrapper = styled.div`
  margin-top: 50px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 12px;
`;

const Title = styled.h4``;

const AddButton = styled(Button)`
  background-color: #27348b;
`;

const TableContainer = styled.div``;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const Action = styled(Box)({
  paddingTop: '0.3rem',
  paddingBottom: '0.3rem',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '0.5rem',
});
